
import { motion } from 'framer-motion';

// Shared container
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Granular Insights (Horizontal Session Flow)
export const EngagementInsightsVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden font-sans">

                {/* Header NOT needed really as the image is just the list, but I'll add a subtle one or just the list directly for maximum space */}

                <div className="flex-1 flex flex-col justify-center space-y-3">
                    {[
                        { id: "S1", title: "Title & Vision", time: "28m 37s", width: "90%" },
                        { id: "S2", title: "The Problem", time: "26m 47s", width: "85%" },
                        { id: "S3", title: "The Solution", time: "2s", width: "5%" }, // Short duration example
                        { id: "S4", title: "Product Showcase", time: "1h 42m", width: "100%", active: true },
                        { id: "S5", title: "Market Opportunity & Business Model", time: "17s", width: "30%" },
                        { id: "S6", title: "Early Validation & Differentiators", time: "49s", width: "40%" },
                    ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                            {/* ID Label */}
                            <div className="w-6 text-[10px] font-bold text-gray-300 text-right shrink-0">{item.id}</div>

                            {/* Bar Container */}
                            <div className="flex-1 relative h-8 rounded-md bg-transparent flex items-center">
                                {/* The Bar Background */}
                                <motion.div
                                    initial={{ width: 0, opacity: 0 }}
                                    whileInView={{ width: item.width, opacity: 1 }}
                                    transition={{ duration: 1, delay: i * 0.1, ease: "easeOut" }}
                                    className={`absolute inset-y-0 left-0 rounded-md flex items-center px-3 ${item.active ? 'bg-blue-100/80 border border-blue-200' : 'bg-blue-50/50 border border-blue-100/50'}`}
                                >
                                    <span className={`text-[10px] font-bold truncate ${item.active ? 'text-blue-700' : 'text-blue-600'}`}>
                                        {item.title}
                                    </span>
                                </motion.div>
                            </div>

                            {/* Time Label */}
                            <div className="w-12 text-[10px] font-bold text-gray-400 text-right shrink-0">{item.time}</div>
                        </div>
                    ))}

                    {/* Faded items representing 'No data' */}
                    <div className="flex items-center gap-3 opacity-40">
                        <div className="w-6 text-[10px] font-bold text-gray-300 text-right shrink-0">S7</div>
                        <div className="flex-1 h-8 flex items-center px-3 text-[10px] font-bold text-gray-400 italic">
                            The Team (No data)
                        </div>
                    </div>
                </div>

                {/* Cursor Click Animation (simulating interaction) */}
                <motion.div
                    className="absolute bottom-6 right-6 pointer-events-none"
                    initial={{ scale: 1 }}
                    animate={{ scale: [1, 0.9, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    <svg className="w-8 h-8 text-blue-500 fill-blue-500/20 drop-shadow-lg" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zm-7.518-.267A8.25 8.25 0 1120.25 10.5M8.288 14.212A5.25 5.25 0 1117.25 10.5" />
                    </svg>
                </motion.div>

            </div>
        </VisualContainer>
    );
};

// Component 2: Viewer Demographics
export const DemographicsVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">

                {/* Tabs */}
                <div className="flex w-full bg-gray-50 p-1 rounded-md mb-6">
                    <div className="flex-1 bg-white shadow-sm rounded-sm py-1 text-center text-[10px] font-bold text-blue-600">Location</div>
                    <div className="flex-1 py-1 text-center text-[10px] font-bold text-gray-400">Device</div>
                </div>

                <div className="flex-1 flex gap-6">
                    {/* List */}
                    <div className="w-1/2 space-y-3">
                        {[
                            { city: "San Francisco", count: 243, pct: 45 },
                            { city: "New York", count: 187, pct: 32 },
                            { city: "London", count: 96, pct: 18 },
                        ].map((loc, i) => (
                            <div key={i} className="space-y-1">
                                <div className="flex justify-between text-[10px]">
                                    <span className="font-bold text-gray-700">{loc.city}</span>
                                    <span className="text-gray-400">{loc.count}</span>
                                </div>
                                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-blue-500 rounded-full"
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${loc.pct}%` }}
                                        transition={{ duration: 1, delay: i * 0.2 }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Donut Chart */}
                    <div className="w-1/2 flex items-center justify-center relative">
                        <svg viewBox="0 0 100 100" className="w-24 h-24 transform -rotate-90">
                            {/* Segment 1 */}
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#e5e7eb" strokeWidth="12" />
                            <motion.circle
                                cx="50" cy="50" r="40" fill="none" stroke="#3b82f6" strokeWidth="12"
                                strokeDasharray="251.2"
                                strokeDashoffset="251.2"
                                initial={{ strokeDashoffset: 251.2 }}
                                whileInView={{ strokeDashoffset: 251.2 * 0.4 }} // 60% filled
                                transition={{ duration: 1.5 }}
                            />
                        </svg>
                        <div className="absolute text-center">
                            <div className="text-xl font-bold text-gray-900">890</div>
                            <div className="text-[8px] text-gray-400 tracking-wider">VIEWS</div>
                        </div>
                    </div>
                </div>

                {/* Live Badge */}
                <div className="absolute top-4 right-4 flex items-center gap-1.5 bg-green-50 text-green-700 px-2 py-1 rounded-full text-[8px] font-bold border border-green-100">
                    <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500"></span>
                    </span>
                    LIVE DATA
                </div>

            </div>
        </VisualContainer>
    );
};
